import {
    ChangeDetectorRef,
    Component,
    DoCheck,
    ElementRef,
    Self,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2,
    SimpleChanges,
    ViewEncapsulation,
    HostListener,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { LocaleService, LocaleState } from '@eui/core';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { CleaveOptions } from 'cleave.js/options';
import Cleave from 'cleave.js';
import { CleaveEventOnChange } from './models/cleave-event-onchange.model';
import { InputDirective } from '../../input.directive';
import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'input[euiInputNumber]:not([euiInputCheckBox])',
    styleUrls: ['./styles/_index.scss'],
    template: '',
    encapsulation: ViewEncapsulation.None,
})
export class EuiInputNumberComponent extends InputDirective implements OnInit, OnDestroy, DoCheck, OnChanges, ControlValueAccessor {

    @Input()
    public get isInvalid() {
        return this._isInvalid || null;
    }
    public set isInvalid(state: any) {
        this.setInvalid(state);
    }

    @HostBinding('class')
    public get cssClasses(): any {
        return [
            super.getCssClasses('eui-input-number'),
            this._isInvalid ? `eui-input-number--invalid` : '',
        ].join(' ').trim();
    }
    /** how many digits for the decimal part */
    @Input()
    get fractionDigits() { return this._fractionDigits; }
    set fractionDigits(value: any) { this._fractionDigits = coerceNumberProperty(value); }
    /** how many digits for the integer part */
    @Input()
    get digits() { return this._digits; }
    set digits(value: any) { this._digits = coerceNumberProperty(value); }
    @Input()
    get fillFraction() { return this._fillFraction; }
    set fillFraction(value: any) { this._fillFraction = coerceBooleanProperty(value); }
    protected _isInvalid: boolean;
    @Input() styleClass: string;
    @HostBinding('attr.type') protected type = 'eui-number';

    /** holds instance of cleave */
    private cleaveInstance: Cleave;
    /** options for the Cleave instance */
    private options: CleaveOptions;
    private _fractionDigits = 0;
    private _digits: number;
    private _fillFraction: boolean;

    constructor(
        @Optional() @Self() protected control: NgControl,
        protected _elementRef: ElementRef,
        protected _renderer: Renderer2,
        private cd: ChangeDetectorRef,
        private localeService: LocaleService) {
        super(_elementRef, _renderer, control);

        // set default options
        this.options = {
            numeral: true,
        };
    }

    ngOnInit() {
        super.ngOnInit();
        // set controlValueAccessor
        if (this.control) {
            this.control.valueAccessor = this;
        }
        // instantiate cleave
        this.initCleave();

        // subscribe to localization service and listen for locale change. De-construction of locale should happen here!
        this.localeService.getState().subscribe( (state: LocaleState) => {
            this.options = this.getCleaveOptsBasedOnLocale(state?.id);

            // update the options on the cleave instance
            this.updateOptions(true);
        });
    }

    ngDoCheck() {
        if (this.control) {
            this.isInvalid = this.control.invalid && this.control.touched;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('fractionDigits')) {
            this.options = { ...this.options, numeralDecimalScale: this.fractionDigits };
            this.updateOptions();
        }
        if (changes.hasOwnProperty('fillFraction') && this.cleaveInstance) {
            this.applyFractionFill(this.cleaveInstance.getRawValue());
        }
        if (changes.hasOwnProperty('digits')) {
            this.options = { ...this.options, numeralIntegerScale: this.digits };
            this.updateOptions();
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        // destroy cleave instance
        if (this.cleaveInstance) {
            this.cleaveInstance.destroy();
        }
    }

    onChange: any = (_) => {
        if (this.control && (_ !== null || _ !== undefined)) {
            // parse number
            const value = Number(_);
            // run on next MicroTask to avoid NG0100 error TODO: deeper investigation
            Promise.resolve().then( () => this.control.viewToModelUpdate(value));
            // in case control is FormControl
            this.control?.control.setValue(isNaN(value) ? null : value, { emitModelToViewChange: false });
        }
    }
    onTouched: any = () => {
        this.isInvalid = this.control.invalid;
    }

    /**
     * sets the number for cleave instance and div element (read only mode)
     * @param val the number formatted or not
     */
    writeValue(val: string): void {
        if (this.cleaveInstance) {
            if (this.fillFraction && val !== '' && val) {
                this.applyFractionFill(val);
                this.cleaveInstance.setRawValue(val);
            } else {
                this.cleaveInstance.setRawValue(val);
            }
            // retrieve formatted value (based on options) and set the number to the model
            const number = Number.parseFloat(this.cleaveInstance.getRawValue());
            this.control.viewToModelUpdate(number);
            this.control?.control.setValue(isNaN(number) ? null : number, { emitModelToViewChange: false });
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    @HostListener('keydown', ['$event'])
    protected onKeyDown(e: KeyboardEvent) {
        // check if it's a single symbol and not Special like (Ctrl, backspace etc)
        if (e.key.length <= 1 && !e.ctrlKey && !e.altKey && !e.shiftKey && !e.metaKey) {
            // stop event propagation if key pressed is not a number or locale character or minus sign
            if (Number.isNaN(Number.parseInt(e.key, 10)) && e.key !== '-' &&
                (e.key !== getLocaleNumberSymbol(this.localeService.currentLocale, NumberSymbol.Decimal))) {
                e.stopPropagation();
                e.preventDefault();
            }
        }

        // protect decimal symbol deletion in case fillFraction is on
        if ((e.key === 'Delete' || e.key === 'Backspace') && this.fillFraction) {
            const element = this._elementRef.nativeElement;
            // position of caret
            const pos = element.selectionStart;
            // get character about to be deleted
            const char = element.value.charAt(pos - 1);
            // in case the char is the decimal symbol prevent deletion and move caret in front
            if (char === getLocaleNumberSymbol(this.localeService.currentLocale, NumberSymbol.Decimal)) {
                e.stopPropagation();
                e.preventDefault();
                element.setSelectionRange(pos - 1, pos - 1);
            }
        }
    }

    protected setInvalid(state?: any): void {
        // in case it's controlled by NgControl override
        this._isInvalid = this.control ? this.control.invalid && this.control.touched : coerceBooleanProperty(state);

        // set BaseDirective Attribute
        super.euiDanger = this._isInvalid;
    }

    /**
     * @Override setPlaceholder setPlaceholderAttribute of InputDirective
     * Updates the placeholder value based on current number format and given value.
     * If value passed contains characters it doesn't format placeholder.
     * */
    protected setPlaceholderAttribute(value: string | number = 999999.9999): void {
        // if value is a number then format the value based on current number format
        if (typeof value === 'number' && !isNaN(value)) {
            const div = document.createElement('input');
            // create a clone of cleaveInstance
            const cloneInstance: Cleave = new Cleave(div, this.options);
            // BEWARE: by the time your setRawValue cleave options have changed thus number formatting
            cloneInstance.setRawValue(value.toString());
            // set the value with current format as placeholder
            super.setPlaceholderAttribute(cloneInstance.getFormattedValue());
        } else if (typeof value === 'string') {
            super.setPlaceholderAttribute(value.toString());
        } else if (isNaN(value)) {
            value = this._elementRef.nativeElement.placeholder;
            super.setPlaceholderAttribute(value.toString());
        }
    }

    /**
     * instantiate Cleave.js
     */
    private initCleave(): void {
        // change the cleave prototype function to intercept onCut callback
        // and allow based on the event bubbling and disabled state
        // TIP: change behavior of onCut to stop in case bubble is canceled
        // TODO: See relevant issue https://github.com/nosir/cleave.js/issues/675
        const _onCut = Cleave.prototype['onCut'];
        Cleave.prototype['onCut'] = function(e: Event) {
            // tslint:disable-next-line:no-invalid-this
            if (!e.cancelBubble && !this.element.disabled) {
                // tslint:disable-next-line:no-invalid-this
                _onCut.call(this, e);
            }
        };

        // in case options is undefined
        const opts: CleaveOptions = { ...this.options };

        // set the onValue change listener to keep the value at readOnly element in sync
        opts.onValueChanged = this.onValueChanged.bind(this);

        // create the Cleave instance with the new or updated options
        this.cleaveInstance = new Cleave(this._elementRef.nativeElement, opts);
    }

    /**
     * Updates the cleave options by destroying the current instance and creating another one while keeping the previous
     * value.
     */
    private updateOptions(localeUpdated: boolean = false): void {
        if (this.cleaveInstance) {
            // the value before destroying cleave instance
            const previousValue = this.cleaveInstance.getRawValue();

            // destroy previous instance from the element
            this.cleaveInstance.destroy();
            // while creating the Cleave instance we are passing onValueChanged
            // and bind to 'this' reference. Inside onValueChanged there is a call
            // to this.cleaveInstance which was using the old one, causing loop (max call stack)
            // So it is essential to set it to null before creating a new instance.
            this.cleaveInstance = null;

            // create the Cleave instance with the new or updated options
            this.cleaveInstance = new Cleave(this._elementRef.nativeElement, {
                ...this.options,
                initValue: previousValue,
                onValueChanged: this.onValueChanged.bind(this),
            });

            // In case previous settings where numeralDecimalMark='.' with value 1000.99 and they changed to
            // numeralDecimalMark=','; set initValue to 1000.99 or even 1000,99 will not be reflected and cleave will
            // remove the decimal part. Set of the value after initialization causes reformat of number and works.
            this.cleaveInstance.setRawValue(previousValue);

            // parse number
            const number = this.parseNumber(this._elementRef.nativeElement.placeholder || undefined, localeUpdated);
            // set placeholder in case none is provided
            this.setPlaceholderAttribute(number);
        }
    }

    /**
     * Callback function of the Cleave instance that is invoked when value is changed.
     * @param event type of CleaveEventOnChange
     */
    private onValueChanged(event: CleaveEventOnChange) {
        const { target } = event;

        if (this.fillFraction) {
            this.applyFractionFill(target.rawValue);
        }
        this.onChange(Number.parseFloat(target.rawValue));
    }

    /**
     * responsible to extract all locale information needed to build CleaveOptions related to number format.
     * @param id The id of the locale
     */
    private getCleaveOptsBasedOnLocale(id: string): CleaveOptions {
        if (!id || id === '') {
            throw new Error('Locale id cannot be empty or undefined');
        }
        const opts: CleaveOptions = {
            numeral: true,
            delimiter: getLocaleNumberSymbol(id, NumberSymbol.Group),
            numeralDecimalMark: getLocaleNumberSymbol(id, NumberSymbol.Decimal),
            numeralDecimalScale: this.fractionDigits,
            numeralIntegerScale: this.digits,
            numeralThousandsGroupStyle: 'thousand', // TODO: implement based on locale
        };

        return { ...this.options, ...opts };
    }

    /**
     * Applies the fill of decimal part with zeros in case decimal part of given number has
     * size less than the max fraction digits.
     * @param number
     * @private
     */
    private applyFractionFill(number: string): void {
        // if fill fraction is enable and there's cleave instance
        if (this.fillFraction) {
            if (this.cleaveInstance) {
                number = number?.toString();
                if (number === '' || !number) {
                    return;
                }
                const hasDecimal = number.split('.').length > 1;
                // in case there's already decimal point in number
                if (hasDecimal) {
                    const decimalSize = number.split('.')[1].length;
                    // if size of decimal part of number is less than the max fraction digits
                    if (decimalSize < this._fractionDigits) {
                        number = number + new Array(this._fractionDigits - decimalSize).fill(0).join('');
                        const pos = this._elementRef.nativeElement.selectionStart;
                        this.cleaveInstance.setRawValue(number);
                        this._elementRef.nativeElement.setSelectionRange(pos, pos);
                    }
                    // in case there's not, force it by filling it as many zero as the fraction digits
                } else if (this.fractionDigits > 0) {
                    number = `${number}.${new Array(this._fractionDigits).fill(0).join('')}`;
                    const pos = this._elementRef.nativeElement.selectionStart;
                    this.cleaveInstance.setRawValue(number);
                    this._elementRef.nativeElement.setSelectionRange(pos, pos);
                }
            }
        }
    }

    /**
     * Parses a given formatted number and returns it as a number.
     * @param value The formatted number you want to parse e.g. 111,888.22
     * @param currentLocaleState Use previous locale state to parse the value. By default is false
     * @private
     */
    private parseNumber(value: string, userPrevLocaleState: boolean = false): number {
        // locale state
        const locale = userPrevLocaleState ? this.localeService.previousLocale : this.localeService.currentLocale;
        // get decimal and group symbols
        let decimalSymbol = getLocaleNumberSymbol(locale, NumberSymbol.Decimal);
        let groupSymbol = getLocaleNumberSymbol(locale, NumberSymbol.Group);
        // replace symbols to parse number
        let parsedNumber = value?.split(groupSymbol).join('').split(decimalSymbol).join('.');
        return Number.parseFloat(parsedNumber);
    }
}
